import apiBase from "./config/apiBase";

/**
 * Auth endpoints:
 * - In local dev/test we want SAME-ORIGIN (CRA proxy) whenever AUTH base is "relative" or not set.
 * - Only use an explicit AUTH server base when REACT_APP_AUTH_API_BASE is set to an absolute URL.
 */

const LEGACY_AUTH_BASE =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_AUTH_API_BASE) ||
  "";

// Normalize auth base:
//  - "relative" or "" => ""
//  - "local"          => ""
//  - "https://..."    => used as-is
function normalizeAuthBase(raw) {
  const r = String(raw || "").trim();
  if (!r) return "";
  const v = r.toLowerCase();
  if (v === "relative") return "";
  if (v === "local") return "";
  if (v.startsWith("http://") || v.startsWith("https://")) return r.replace(/\/$/, "");
  return "";
}

const AUTH_BASE = normalizeAuthBase(LEGACY_AUTH_BASE);

// -----------------------------------------------------------------------------
// Session (SID) helpers
// -----------------------------------------------------------------------------
const SESSION_COOKIE_NAME =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_SESSION_COOKIE_NAME) ||
  "br_session_id";
const CSRF_COOKIE_NAME = "br_csrf_token";
const CSRF_HEADER_NAME = "X-BR-CSRF";

let _sessionStartPromise = null;

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

function newSID() {
  try {
    return crypto.randomUUID();
  } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function readSID() {
  try {
    return (
      window.localStorage.getItem("br_sid") ||
      getCookie(SESSION_COOKIE_NAME) ||
      null
    );
  } catch {
    return getCookie(SESSION_COOKIE_NAME);
  }
}

function saveSID(sid) {
  if (!sid) return;
  try {
    window.localStorage.setItem("br_sid", sid);
  } catch {
    // ignore
  }
}

function clearSID() {
  try {
    window.localStorage.removeItem("br_sid");
  } catch {
    // ignore
  }
}

// -----------------------------
// Internal helpers
// -----------------------------
function buildSidHeaders(initHeaders = {}) {
  const h = new Headers(initHeaders);
  const sid = readSID();
  if (sid) {
    h.set("X-Session-ID", sid);
    h.set("X-Session-Id", sid);
  }
  return h;
}

function isAuthPath(path) {
  return (
    typeof path === "string" &&
    /^\/api\/(login|logout|register|refresh|userinfo)\b/i.test(path)
  );
}

function buildURL(path) {
  if (/^https?:\/\//i.test(path)) return path;

  // Login and all account features must use one public API origin. An HttpOnly
  // cookie set by a different host cannot authenticate product requests and is
  // especially unreliable in browsers that block third-party cookies.
  if (isAuthPath(path)) {
    const base = apiBase || AUTH_BASE;
    return base ? `${base}${path}` : path;
  }

  // Non-auth routes always use apiBase or same-origin relative.
  return apiBase ? `${apiBase}${path}` : path;
}

function resolveFetchMode(url) {
  if (!url || typeof window === "undefined") return "cors";
  try {
    const target = new URL(url, window.location.origin);
    return target.origin === window.location.origin ? "same-origin" : "cors";
  } catch {
    return "cors";
  }
}

function updateSIDFromResponse(res, fallbackSID = null) {
  if (!res) return;

  const echoed =
    res.headers.get("X-Session-ID") ||
    res.headers.get("X-Session-Id") ||
    res.headers.get("x-session-id");

  if (echoed) {
    saveSID(echoed);
    return;
  }

  const setCookie = res.headers.get("X-Set-Session-ID");
  if (setCookie) {
    saveSID(setCookie);
    return;
  }

  if (fallbackSID) saveSID(fallbackSID);
}

// -----------------------------------------------------------------------------
// Consent bootstrap (API server owns these endpoints)
// -----------------------------------------------------------------------------
export async function acceptConsent() {
  const sid = readSID();

  let r = await withCreds("/privacy/consent/accept", {
    method: "POST",
    body: JSON.stringify({ session_id: sid }),
  }).catch(() => null);

  if (r && r.ok) {
    updateSIDFromResponse(r, sid);
    return true;
  }

  const u = await getUserInfo().catch(() => null);
  const user_id = u && (u.sub || u.user_id || u.uid);

  r = await withCreds("/privacy/consent", {
    method: "POST",
    body: JSON.stringify({ consent: "opt_in", user_id, session_id: sid }),
  }).catch(() => null);

  if (r && r.ok) {
    updateSIDFromResponse(r, sid);
    return true;
  }

  return false;
}

// -----------------------------------------------------------------------------
// Unified fetch (cookies + SID)
// -----------------------------------------------------------------------------
export async function withCreds(
  path,
  {
    method = "GET",
    headers = {},
    body,
    signal,
    keepalive = false,
    _retryOnce = false,
    _skipRefresh = false,
  } = {}
) {
  let sid = readSID();
  if (!sid) {
    sid = newSID();
    saveSID(sid);
  }

  const h = buildSidHeaders(headers);
  const unsafeMethod = !["GET", "HEAD", "OPTIONS"].includes(String(method).toUpperCase());
  const csrf = getCookie(CSRF_COOKIE_NAME);
  if (unsafeMethod && csrf) h.set(CSRF_HEADER_NAME, csrf);
  if (body != null && !h.has("Content-Type")) {
    h.set("Content-Type", "application/json");
  }

  const url = buildURL(path);

  const res = await fetch(url, {
    method,
    headers: h,
    body,
    signal,
    keepalive,
    credentials: "include",
    mode: resolveFetchMode(url),
  });

  updateSIDFromResponse(res, sid);

  if (res.status === 401 && !_retryOnce && !_skipRefresh && !isAuthPath(path)) {
    const refreshed = await refreshToken().catch(() => ({ ok: false }));
    if (refreshed?.ok) {
      return withCreds(path, {
        method,
        headers,
        body,
        signal,
        keepalive,
        _retryOnce: true,
        _skipRefresh: true,
      });
    }
    clearSID();
  }

  return res;
}

// -----------------------------------------------------------------------------
// Session bootstrap
// -----------------------------------------------------------------------------
export async function startSession() {
  if (_sessionStartPromise) return _sessionStartPromise;

  _sessionStartPromise = (async () => {
    try {
      const sid = readSID() || newSID();
      saveSID(sid);

      const res = await withCreds("/session_start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid }),
      });

      if (!res.ok) return readSID();

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      const next =
        data?.session_id ||
        res.headers.get("X-Session-ID") ||
        res.headers.get("X-Session-Id") ||
        sid;

      if (next) saveSID(next);
      return readSID();
    } catch {
      return readSID();
    } finally {
      _sessionStartPromise = null;
    }
  })();

  return _sessionStartPromise;
}

export async function ensureSession() {
  return startSession();
}

// -----------------------------------------------------------------------------
// Auth (cookie truth)
// -----------------------------------------------------------------------------
export async function getUserInfo() {
  const r = await withCreds("/api/userinfo");
  if (!r.ok) {
    return { success: false, authenticated: false, sub: "anon" };
  }

  const data = await r.json().catch(() => ({}));
  if (data && typeof data.session_id === "string" && data.session_id) {
    saveSID(data.session_id);
  }
  return data;
}

export async function refreshToken() {
  try {
    const r = await withCreds("/api/refresh", { method: "POST", _skipRefresh: true });
    if (!r.ok) return { ok: false };
    const data = await r.json().catch(() => ({}));
    return data && typeof data === "object" ? data : { ok: true };
  } catch {
    return { ok: false };
  }
}

// -----------------------------------------------------------------------------
// Preferences
// -----------------------------------------------------------------------------
export async function getUserPreferences() {
  const r = await withCreds("/api/preferences");
  if (!r.ok) throw new Error("Failed to load preferences");
  const data = await r.json().catch(() => ({}));
  return data?.preferences && typeof data.preferences === "object"
    ? data.preferences
    : data;
}

export async function updateUserPreferences(partialPrefs = {}) {
  try {
    let res = await withCreds("/api/preferences", {
      method: "PATCH",
      body: JSON.stringify(partialPrefs),
    });

    if (res.status === 405 || res.status === 404) {
      res = await withCreds("/api/preferences", {
        method: "PUT",
        body: JSON.stringify(partialPrefs),
      });
    }

    const text = await res.text().catch(() => "");
    let parsed = {};
    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {
      parsed = { message: text };
    }

    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        message: parsed?.message || "Failed to update preferences",
      };
    }

    return { ok: true, ...parsed };
  } catch (e) {
    return { ok: false, message: e?.message || "Network error" };
  }
}

// -----------------------------------------------------------------------------
// Activity
// -----------------------------------------------------------------------------
export async function getActivity() {
  try {
    const res = await withCreds("/api/activity");
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.items)) return data.items;
    return [];
  } catch {
    return [];
  }
}

// -----------------------------------------------------------------------------
// Registration
// -----------------------------------------------------------------------------
export async function registerUser({ username, password, tenantId = "default", saveConversationHistory = false } = {}) {
  const res = await withCreds("/api/register", {
    method: "POST",
    body: JSON.stringify({ username, password, tenantId, save_conversation_history: saveConversationHistory === true }),
  });

  const data = await res.json().catch(() => ({}));
  return {
    success: !!(data.ok || data.authenticated || data.success),
    data,
    httpOk: res.ok,
  };
}

// -----------------------------------------------------------------------------
// Logout
// -----------------------------------------------------------------------------
export async function logoutUser() {
  try {
    const res = await withCreds("/api/logout", { method: "POST" });
    clearSID();
    return res.ok;
  } catch {
    clearSID();
    return false;
  }
}

export async function logoutAllDevices() {
  try {
    const res = await withCreds("/api/logout-all", { method: "POST" });
    if (res.ok) clearSID();
    return res.ok;
  } catch {
    return false;
  }
}

async function decodeSecurityResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "security_request_failed");
  return body;
}

export async function listActiveSessions() {
  return withCreds("/api/security/sessions").then(decodeSecurityResponse);
}

export async function revokeActiveSession(sessionId) {
  return withCreds(`/api/security/sessions/${encodeURIComponent(sessionId)}`, {
    method: "DELETE",
  }).then(decodeSecurityResponse);
}

export async function listSecurityEvents() {
  return withCreds("/api/security/events").then(decodeSecurityResponse);
}

export async function changePassword(currentPassword, newPassword) {
  const result = await withCreds("/api/security/password", {
    method: "POST",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  }).then(decodeSecurityResponse);
  clearSID();
  return result;
}

export async function requestPasswordRecovery(email) {
  return withCreds("/api/password-recovery/request", {
    method: "POST",
    body: JSON.stringify({ email }),
  }).then(decodeSecurityResponse);
}

export async function resetPassword(token, newPassword) {
  return withCreds("/api/password-recovery/reset", {
    method: "POST",
    body: JSON.stringify({ token, new_password: newPassword }),
  }).then(decodeSecurityResponse);
}

export const _sid = { readSID, saveSID, clearSID, newSID };
